import { faPrint } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, IconButton, MenuItem, Select, Stack, Tooltip } from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import type { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"

import { FilterSearch } from "../../../../../shared/components/createOrder/components/FilterSearch"
import { StandardCountFilterChip } from "../../../../../shared/components/filters/StandardCountFilterChip"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { StandardSegmentedControl } from "../../../../../shared/components/segmentedControl/StandardSegmentedControl"
import { HIDDEN_IN_PRINT } from "../../../../../shared/components/print/printPageOnly"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { usePrintPageOnly } from "../../../../../shared/hooks/usePrintPageOnly"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"

import { ALL_FLOORS, MeetingRoomStatusFilter, MeetingRoomsView } from "../meetingRoomsTypes"
import type { MeetingRoomStatusCounts } from "../meetingRoomsTypes"
import { MeetingRoomsDayNavigator } from "./MeetingRoomsDayNavigator"

const STATUS_FILTER_OPTIONS = Object.values(MeetingRoomStatusFilter)

const useStyles = makeStyles()((theme) => ({
    toolbar: HIDDEN_IN_PRINT,
    row: {
        flexWrap: "wrap",
    },
    actions: {
        marginLeft: "auto",
    },
    iconButton: {
        width: "3rem",
        height: "3rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        background: theme.palette.background.paper,
        color: theme.palette.tailwind.slate[800],
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    searchWrap: {
        "& .MuiOutlinedInput-root.MuiOutlinedInput-root": {
            height: "3rem",
            boxShadow: "none",
            background: theme.palette.background.paper,
        },
    },
    floorSelect: {
        minWidth: "10rem",
        height: "3rem",
        borderRadius: ".75rem",
        background: theme.palette.background.paper,
    },
    clearButton: {
        textTransform: "none",
        color: theme.palette.primary.main,
    },
}))

type Props = Readonly<{
    date: DateTime
    view: MeetingRoomsView
    hourLabel: string
    statusFilter: MeetingRoomStatusFilter
    statusCounts: MeetingRoomStatusCounts
    floor: string
    floorNames: ReadonlyArray<string>
    search: string
    isPrintAndExportDisabled: boolean
    onDateChange: (date: DateTime) => void
    onViewChange: (view: MeetingRoomsView) => void
    onStatusFilterChange: (filter: MeetingRoomStatusFilter) => void
    onFloorChange: (floor: string) => void
    onSearchChange: (search: string) => void
    onReset: () => void
    onExport: () => void
}>

export function MeetingRoomsToolbar({
    date,
    view,
    hourLabel,
    statusFilter,
    statusCounts,
    floor,
    floorNames,
    search,
    isPrintAndExportDisabled,
    onDateChange,
    onViewChange,
    onStatusFilterChange,
    onFloorChange,
    onSearchChange,
    onReset,
    onExport,
}: Props) {
    const { classes } = useStyles()
    const printPageOnly = usePrintPageOnly()

    const isFiltered = statusFilter !== MeetingRoomStatusFilter.ALL || floor !== ALL_FLOORS || search.trim() !== ""

    const viewOptions = [
        {
            value: MeetingRoomsView.STATUS,
            label: (
                <LocalizedStrict id="service-center-meeting-rooms-view-status">
                    <span>Status</span>
                </LocalizedStrict>
            ),
            badge: hourLabel,
        },
        {
            value: MeetingRoomsView.BY_HOUR,
            label: (
                <LocalizedStrict id="service-center-meeting-rooms-view-by-hour">
                    <span>By hour</span>
                </LocalizedStrict>
            ),
        },
        {
            value: MeetingRoomsView.BY_ROOM,
            label: (
                <LocalizedStrict id="service-center-meeting-rooms-view-by-room">
                    <span>By room</span>
                </LocalizedStrict>
            ),
        },
    ]

    return (
        <Stack spacing="1rem" className={classes.toolbar}>
            <Stack direction="row" alignItems="center" spacing="1rem" useFlexGap className={classes.row}>
                <MeetingRoomsDayNavigator date={date} onDateChange={onDateChange} />
                <StandardSegmentedControl options={viewOptions} value={view} onChange={onViewChange} color="dark" />
                <Stack direction="row" alignItems="center" spacing=".75rem" className={classes.actions}>
                    {isFiltered && (
                        <LocalizedStrict id="service-center-meeting-rooms-reset-filters-button">
                            <StandardButton
                                variant="text"
                                size="small"
                                className={classes.clearButton}
                                onClick={onReset}
                            >
                                Reset
                            </StandardButton>
                        </LocalizedStrict>
                    )}
                    <Box className={classes.searchWrap}>
                        <FilterSearch
                            search={search}
                            setSearch={onSearchChange}
                            activeWidth="18rem"
                            inactiveWidth="3rem"
                            placeholderId="service-center-meeting-rooms-search-input"
                        />
                    </Box>
                    <Select
                        value={floor}
                        onChange={(event: SelectChangeEvent<string>) => onFloorChange(event.target.value)}
                        className={classes.floorSelect}
                    >
                        <MenuItem value={ALL_FLOORS}>
                            <LocalizedStrict id="service-center-meeting-rooms-floor-filter-all">
                                <>All floors</>
                            </LocalizedStrict>
                        </MenuItem>
                        {floorNames.map((floorName) => (
                            <MenuItem key={floorName} value={floorName}>
                                {floorName}
                            </MenuItem>
                        ))}
                    </Select>
                    <LocalizedStrict id="service-center-meeting-rooms-print-button" attrs={{ title: true }}>
                        <Tooltip title="Print">
                            <Box component="span">
                                <LocalizedStrict
                                    id="service-center-meeting-rooms-print-button"
                                    attrs={{ "aria-label": true }}
                                >
                                    <IconButton
                                        aria-label="Print"
                                        className={classes.iconButton}
                                        disabled={isPrintAndExportDisabled}
                                        onClick={printPageOnly}
                                    >
                                        <FontAwesomeIcon icon={faPrint} />
                                    </IconButton>
                                </LocalizedStrict>
                            </Box>
                        </Tooltip>
                    </LocalizedStrict>
                    <StandardExportButton
                        bordered
                        className={classes.iconButton}
                        disabled={isPrintAndExportDisabled}
                        onClick={onExport}
                    />
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing=".75rem" useFlexGap className={classes.row}>
                {STATUS_FILTER_OPTIONS.map((option) => (
                    <StandardCountFilterChip
                        key={option}
                        isSelected={option === statusFilter}
                        count={statusCounts[option]}
                        onClick={() => onStatusFilterChange(option)}
                    >
                        <LocalizedEnum base="service-center-meeting-rooms-status-filter" enumValue={option}>
                            <>{option}</>
                        </LocalizedEnum>
                    </StandardCountFilterChip>
                ))}
            </Stack>
        </Stack>
    )
}
